import { EffectsCommandMap } from 'dva';
import { AVATAR_URL_PREFIX } from '@/constants';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI } from '@freelog/tools-lib';
import { history } from '@@/core/history';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import dayjs from 'dayjs';
import type {
  ChangeAction,
  FetchInfoAction,
  Fetch_CollectionItems_Action,
} from './types';
import type { CollectionExhibitInfoPageModelState } from './types';
import { CollectionDetailPageModelState } from '@/models/collectionDetailPage';

export const fetchEffects = {
  *fetchInfo({}: FetchInfoAction, { call, select, put }: EffectsCommandMap) {
    const { collectionExhibitInfoPage }: ConnectState = yield select(
      ({ collectionExhibitInfoPage }: ConnectState) => ({
        collectionExhibitInfoPage,
      }),
    );

    if (!collectionExhibitInfoPage.exhibit_ID) {
      return;
    }

    const params: Parameters<
      typeof FServiceAPI.Exhibit.presentableDetails
    >[0] = {
      presentableId: collectionExhibitInfoPage.exhibit_ID,
      isLoadCustomPropertyDescriptors: 1,
      isLoadPolicyInfo: 1,
      isTranslate: 1,
    };

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_ID: '',
      },
    });
    const {
      data: data_PresentableDetails,
    }: {
      data: {
        nodeId: number;
        userId: number;
        presentableId: string;
        presentableName: string;
        resourceInfo: any;
        resourceCustomPropertyDescriptors: {
          candidateItems: string[];
          defaultValue: string;
          key: string;
          name: string;
          remark: string;
          type: 'readonlyText' | 'select' | 'editableText';
        }[];
        coverImages: string[];
        onlineStatus: number;
        policies: any[];
        presentableTitle: string;
        presentableIntro: string;
        resolveResources: any[];
        tags: string[];
        version: string;
        resourceSystemProperty: { [k: string]: string };
        resourceSystemPropertyDescriptors: {
          defaultValue: number | string;
          key: string;
          name: string;
          remark: string;
          valueDisplay: string;
          valueUnit: string;
        }[];
        presentableRewriteProperty: {
          key: string;
          remark: string;
          value: string;
        }[];
      };
    } = yield call(FServiceAPI.Exhibit.presentableDetails, params);
    if (
      !data_PresentableDetails ||
      data_PresentableDetails.userId !== FUtil.Tool.getUserIDByCookies()
    ) {
      history.replace(FUtil.LinkTo.exception403({}));
      return;
    }

    const params3: Parameters<typeof FServiceAPI.Node.details>[0] = {
      nodeId: data_PresentableDetails.nodeId,
    };

    const { data: data_NodeDetails } = yield call(
      FServiceAPI.Node.details,
      params3,
    );

    if (data_NodeDetails?.status === -1) {
      history.replace(
        FUtil.LinkTo.nodeDeleted({ nodeID: data_PresentableDetails.nodeId }),
      );
      return;
    }

    if ((data_NodeDetails.status & 4) === 4) {
      history.replace(
        FUtil.LinkTo.nodeFreeze({ nodeID: data_PresentableDetails.nodeId }),
      );
      return;
    }

    const params2: Parameters<typeof FServiceAPI.Resource.info>[0] = {
      resourceIdOrName: data_PresentableDetails.resourceInfo.resourceId,
    };

    const {
      data: data_ResourceInfo,
    }: {
      data: {
        resourceId: string;
        coverImages: string[];
        resourceName: string;
        resourceTitle: string;
        resourceTypeCode: string;
        resourceType: string[];
        updateDate: string;
        status: 0 | 1 | 2 | 4;
        latestVersion: string;
        resourceVersions: { version: string }[];
        policies: PolicyFullInfo_Type[];
        username: string;
        userId: number;
        operationType: 0 | 1 | 2 | 3 | 4 | 5;
        subjectType: number;
        serializeStatus: number;
      };
    } = yield call(FServiceAPI.Resource.info, params2);

    const disabledRewriteKeys = [
      ...data_PresentableDetails.resourceCustomPropertyDescriptors.map(
        (i) => i.key,
      ),
    ];

    const params1: Parameters<typeof FServiceAPI.Exhibit.batchAuth>[0] = {
      nodeId: data_PresentableDetails.nodeId,
      authType: 3,
      presentableIds: data_PresentableDetails.presentableId,
    };
    const { data: data_ExhibitBatchAuthResults } = yield call(
      FServiceAPI.Exhibit.batchAuth,
      params1,
    );

    const policies: PolicyFullInfo_Type[] = [
      ...data_PresentableDetails.policies,
    ];
    policies.reverse();
    policies.sort((a, b) => {
      return a.status === 1 && b.status === 0 ? -1 : 0;
    });

    const param2: Parameters<
      typeof FServiceAPI.Resource.resourceVersionInfo1
    >[0] = {
      version: '1.0.0',
      resourceId: data_ResourceInfo.resourceId,
    };
    const {
      data: data_versionInfo,
    }: {
      data: {
        customPropertyDescriptors: {
          key: string;
          name: string;
          defaultValue: string;
          type:
            | 'editableText'
            | 'readonlyText'
            | 'radio'
            | 'checkbox'
            | 'select';
          candidateItems: string[];
          remark: string;
        }[];
        systemProperty: { [key: string]: string };
        systemPropertyDescriptors: {
          defaultValue: number | string;
          key: string;
          name: string;
          remark: string;
          valueDisplay: string;
          valueUnit: string;
        }[];
        createDate: string;
        description: string;
        catalogueProperty: {
          collection_item_count: number;
          collection_sort_list?:
            | 'collection_sort_ascending'
            | 'collection_sort_descending';
          collection_item_title?:
            | 'collection_item_title_rtitle'
            | 'collection_item_title_sn'
            | 'collection_item_title_empty'
            | 'collection_item_title_custom';
          collection_item_no_display?:
            | 'collection_item_no_display_show'
            | 'collection_item_no_display_hide';
          collection_item_image_display?:
            | 'collection_item_image_display_show'
            | 'collection_item_image_display_hide';
          collection_item_descr_display?:
            | 'collection_item_descr_display_show'
            | 'collection_item_descr_display_hide';
          collection_view?: 'collection_view_list' | 'collection_view_card';
        };
      };
    } = yield call(FServiceAPI.Resource.resourceVersionInfo1, param2);

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        pageLoading: false,
        exhibit_BelongNode_ID: data_PresentableDetails.nodeId,
        exhibit_BelongNode_Name: data_NodeDetails.nodeName,
        exhibit_BelongNode_Title: data_NodeDetails.nodeTitle,
        exhibit_BelongNode_ActiveThemeId: data_NodeDetails.nodeThemeId,
        exhibit_ID: data_PresentableDetails.presentableId,
        exhibit_Name: data_PresentableDetails.presentableName,
        exhibit_Title: data_PresentableDetails.presentableTitle,
        exhibit_Online: data_PresentableDetails.onlineStatus === 1,
        exhibit_IsAuth: data_ExhibitBatchAuthResults
          ? data_ExhibitBatchAuthResults[0].isAuth
          : true,
        exhibit_AuthErrorText: data_ExhibitBatchAuthResults
          ? data_ExhibitBatchAuthResults[0].error
          : '',
        exhibit_Cover: data_PresentableDetails.coverImages[0] || '',
        exhibit_ResourceInfo: {
          id: data_ResourceInfo.resourceId,
          cover: data_ResourceInfo.coverImages[0] || '',
          name: data_ResourceInfo.resourceName,
          title: data_ResourceInfo.resourceTitle,
          version: data_ResourceInfo.latestVersion,
          policy: data_ResourceInfo.policies
            .filter((l) => l.status === 1)
            .map((l) => l.policyName),
          type: data_ResourceInfo.resourceType,
          status: data_ResourceInfo.status,
          authProblem: false,
          updateDate: dayjs(data_ResourceInfo.updateDate).format(FUtil.Predefined.momentDateFormat),
          username: data_ResourceInfo.username,
          useAvatar: `${AVATAR_URL_PREFIX}${data_ResourceInfo.userId}`,
          isChoice: data_ResourceInfo.operationType === 5,
          collection:
            data_ResourceInfo.subjectType === 4
              ? {
                  status:
                    data_ResourceInfo.serializeStatus === 1
                      ? 'finish'
                      : 'unFinish',
                  count:
                    data_versionInfo.catalogueProperty.collection_item_count,
                }
              : null,
        },
        policy_List: policies,
        side_ExhibitTitle: data_PresentableDetails.presentableTitle,
        side_ExhibitIntroduction:
          data_PresentableDetails.presentableIntro || '',
        side_ExhibitTags: data_PresentableDetails.tags,
        side_AllVersions: data_ResourceInfo.resourceVersions.map(
          (d2: any) => d2.version,
        ),
        side_Version: data_PresentableDetails.version,
        side_RawProperties:
          data_PresentableDetails.resourceSystemPropertyDescriptors
            .filter((spd) => spd.defaultValue !== '')
            .map((spd) => ({
              key: spd.key,
              name: spd.name,
              value: spd.valueDisplay,
              description: spd.remark,
            })),
        side_BaseProperties:
          data_PresentableDetails.resourceCustomPropertyDescriptors
            .filter((rd) => rd.type === 'readonlyText' && rd.defaultValue !== '')
            .map((rd: any) => ({
              key: rd.key,
              name: rd.name,
              value: rd.defaultValue,
              description: rd.remark,
            })),
        inheritOptions:
          data_PresentableDetails.resourceCustomPropertyDescriptors
            .filter((rd) => rd.type === 'editableText' || rd.type === 'select')
            .map<
              CollectionExhibitInfoPageModelState['inheritOptions'][number]
            >((rd) => {
              const prp =
                data_PresentableDetails.presentableRewriteProperty.find(
                  (pr) => pr.key === rd.key,
                );
              const value = prp ? prp.value : rd.defaultValue;
              return {
                key: rd?.key || '',
                name: rd?.name || '',
                value: value,
                description: rd.remark || '',
                type: rd.type === 'select' ? 'select' : 'input',
                options: rd.type === 'select' ? rd.candidateItems : [],
                defaultValue: rd.defaultValue || '',
              };
            }),
        customOptions: (
          data_PresentableDetails.presentableRewriteProperty as any[]
        )
          .filter((pr: any) => !disabledRewriteKeys.includes(pr.key))
          .map<CollectionExhibitInfoPageModelState['customOptions'][number]>(
            (pr) => ({
              key: pr.key,
              name: pr.name,
              value: pr.value,
              description: pr.remark,
              valueInput: pr.value,
              valueInputError: '',
            }),
          ),
        collectionItemsSetting: {
          collection_sort_list:
            data_versionInfo.catalogueProperty.collection_sort_list ||
            'collection_sort_ascending',
          collection_item_title:
            data_versionInfo.catalogueProperty.collection_item_title ||
            'collection_item_title_rtitle',
          collection_item_no_display:
            data_versionInfo.catalogueProperty.collection_item_no_display ||
            'collection_item_no_display_show',
          collection_item_image_display:
            data_versionInfo.catalogueProperty
              .collection_item_image_display ||
            'collection_item_image_display_show',
          collection_item_descr_display:
            data_versionInfo.catalogueProperty
              .collection_item_descr_display ||
            'collection_item_descr_display_show',
          collection_view:
            data_versionInfo.catalogueProperty.collection_view ||
            'collection_view_list',
        },
        collectionItems_pageSize:
          data_versionInfo.catalogueProperty.collection_view ===
          'collection_view_card'
            ? 6
            : 10,
      },
    });

    yield put<Fetch_CollectionItems_Action>({
      type: 'fetch_CollectionItems',
    });
  },
  *fetch_CollectionItems(
    {}: Fetch_CollectionItems_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { collectionExhibitInfoPage }: ConnectState = yield select(
      ({ collectionExhibitInfoPage }: ConnectState) => ({
        collectionExhibitInfoPage,
      }),
    );

    let data_collectionItems: {
      createDate: string;
      itemId: string;
      itemTitle: string;
      sortId: number;
      number: string;
      mountResourceInfo: { resourceId: string };
    }[] = [];

    const parmas: Parameters<
      typeof FServiceAPI.Resource.getCollectionItems
    >[0] = {
      resourceId: collectionExhibitInfoPage.exhibit_ResourceInfo?.id || '',
      skip:
        (collectionExhibitInfoPage.collectionItems_pageCurrent - 1) *
        collectionExhibitInfoPage.collectionItems_pageSize,
      limit: collectionExhibitInfoPage.collectionItems_pageSize,
      sortType:
        collectionExhibitInfoPage.collectionItemsSetting
          .collection_sort_list === 'collection_sort_descending'
          ? -1
          : 1,
      isLoadLatestVersionInfo: 1,
    };

    const {
      data,
    }: {
      ret: number;
      errCode: number;
      data: {
        skip: number;
        limit: number;
        totalItem: number;
        dataList: {
          createDate: string;
          itemId: string;
          itemTitle: string;
          sortId: number;
          number: string;
          mountResourceInfo: { resourceId: string };
        }[];
      };
    } = yield call(FServiceAPI.Resource.getCollectionItems, parmas);

    if (data.dataList.length > 0) {
      data_collectionItems = data.dataList;
    } else if (collectionExhibitInfoPage.collectionItems_pageCurrent > 1) {
      yield put<ChangeAction>({
        type: 'change',
        payload: { collectionItems_pageCurrent: 1 },
      });

      const parmas: Parameters<
        typeof FServiceAPI.Resource.getCollectionItems
      >[0] = {
        resourceId: collectionExhibitInfoPage.exhibit_ResourceInfo?.id || '',
        skip:
          (collectionExhibitInfoPage.collectionItems_pageCurrent - 1) *
          collectionExhibitInfoPage.collectionItems_pageSize,
        limit: collectionExhibitInfoPage.collectionItems_pageSize,
        keywords: collectionExhibitInfoPage.collectionItems_keywords,
        isLoadLatestVersionInfo: 1,
      };

      const {
        data: data1,
      }: {
        ret: number;
        errCode: number;
        data: {
          skip: number;
          limit: number;
          totalItem: number;
          dataList: {
            createDate: string;
            itemId: string;
            itemTitle: string;
            sortId: number;
            number: string;
            mountResourceInfo: { resourceId: string };
          }[];
        };
      } = yield call(FServiceAPI.Resource.getCollectionItems, parmas);

      data_collectionItems = data1.dataList;
    }

    let data_resourceList: any[] = [];
    let auths: { itemId: string; isAuth: boolean }[] = [];

    if (data_collectionItems.length > 0) {
      const { data: data_resourceList1 } = yield call(
        FServiceAPI.Resource.batchInfo,
        {
          resourceIds: data_collectionItems
            .map((d) => d.mountResourceInfo.resourceId)
            .join(','),
        },
      );
      data_resourceList = data_resourceList1;

      const parmas1: Parameters<
        typeof FServiceAPI.Resource.getCollectionItemsAuth
      >[0] = {
        resourceId: collectionExhibitInfoPage.exhibit_ResourceInfo?.id || '',
        itemIds: data_collectionItems.map((d) => d.itemId).join(','),
      };
      const {
        data: data1,
      }: {
        data: { itemId: string; isAuth: boolean }[];
      } = yield call(FServiceAPI.Resource.getCollectionItemsAuth, parmas1);
      auths = data1;
    }

    const list: CollectionDetailPageModelState['collectionItems'] =
      data_collectionItems.map<
        CollectionDetailPageModelState['collectionItems'][number]
      >((i) => {
        const resAuth = auths.find((dd) => dd.itemId === i.itemId);
        const resource = data_resourceList.find(
          (r) => r.resourceId === i.mountResourceInfo.resourceId,
        );
        return {
          itemID: i.itemId,
          itemTitle: i.itemTitle,
          itemSerial: i.sortId,
          itemNumber: i.number,
          resourceInfo: {
            id: resource.resourceId,
            cover:
              resource.coverImages.length > 0 ? resource.coverImages[0] : '',
            name: resource.resourceName,
            title: resource.resourceTitle,
            version: resource.latestVersion,
            policy: resource.policies
              .filter((l: any) => l.status === 1)
              .map((l: any) => l.policyName),
            type: resource.resourceType,
            status: resource.status,
            authProblem: !!resAuth && !resAuth.isAuth,
            updateDate: dayjs(resource.updateDate).format(FUtil.Predefined.momentDateFormat),
            username: resource.username,
            useAvatar: `${AVATAR_URL_PREFIX}${resource.userId}`,
            isChoice: resource.operationType === 5,
            intro: resource.intro,
            releaseDateTime: dayjs(
              resource.resourceVersions[0]?.createDate,
            ).format(FUtil.Predefined.momentDateFormat),
          },
        };
      });

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        collectionItems: list,
        collectionItems_inited: true,
        collectionItems_totalCount: data.totalItem,
      },
    });
  },
};
